'use client'

import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { UserType } from '@/types/UserType'
import { clearAuthStorage } from '@/services/storage'
import {
  registerLogoutHandler,
  unregisterLogoutHandler,
  registerLicenseBlockedHandler,
  unregisterLicenseBlockedHandler,
} from '@/services/auth-events'
import { resetLicenseBlockedFlag } from '@/services/api'

interface AuthContextType {
  user: UserType | null
  isAuthenticated: boolean
  isLoading: boolean
  setAuthUser: (user: UserType | null) => void
  clearAuth: () => void
  licenseBlocked: boolean
  licenseBlockedMessage: string | null
  setLicenseBlocked: (message: string | null) => void
  clearLicenseBlocked: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUserState] = useState<UserType | null>(null)
  const [licenseBlockedMessage, setLicenseBlockedMessage] = useState<string | null>(null)
  const isLoading = false

  const setAuthUser = useCallback((nextUser: UserType | null) => {
    setUserState(nextUser)
  }, [])

  const clearAuth = useCallback(() => {
    setUserState(null)
    if (typeof window !== 'undefined') {
      clearAuthStorage()
    }
  }, [])

  const setLicenseBlocked = useCallback((message: string | null) => {
    setLicenseBlockedMessage(message ?? 'Acesso bloqueado. Há faturas em aberto da sua assinatura. Regularize o pagamento para liberar o sistema.')
  }, [])

  const clearLicenseBlocked = useCallback(() => {
    setLicenseBlockedMessage(null)
    resetLicenseBlockedFlag()
  }, [])

  useEffect(() => {
    const handleAuthLogout = () => {
      clearAuth()
    }

    registerLogoutHandler(handleAuthLogout)

    return () => {
      unregisterLogoutHandler(handleAuthLogout)
    }
  }, [clearAuth])

  useEffect(() => {
    registerLicenseBlockedHandler(setLicenseBlocked)

    return () => {
      unregisterLicenseBlockedHandler(setLicenseBlocked)
    }
  }, [setLicenseBlocked])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: Boolean(user),
        setAuthUser,
        clearAuth,
        isLoading,
        licenseBlocked: licenseBlockedMessage !== null,
        licenseBlockedMessage,
        setLicenseBlocked,
        clearLicenseBlocked,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function useSetUser() {
  const { setAuthUser } = useAuth()
  return setAuthUser
}
